<template>
  <div class="import-data-container">
    <div class="page-header">
      <h1>數據導入工具</h1>
      <p class="description">從JSON文件導入鞋子數據到系統中</p>
    </div>

    <el-card class="upload-card">
      <template #header>
        <div class="card-header">
          <h3>上傳JSON數據文件</h3>
        </div>
      </template>
      
      <el-upload
        class="upload-area"
        drag
        :action="'/api/v1/import/upload-json'"
        :headers="uploadHeaders"
        :on-success="handleUploadSuccess"
        :on-error="handleUploadError"
        :before-upload="beforeUpload"
        :show-file-list="false"
        accept=".json"
        :disabled="isUploading">
        <el-icon class="el-icon--upload">
          <i-ep-upload-filled />
        </el-icon>
        <div class="el-upload__text">
          將JSON文件拖到此處，或<em>點擊上傳</em>
        </div>
        <template #tip>
          <div class="el-upload__tip">
            只能上傳JSON文件，且大小不能超過15MB
          </div>
        </template>
      </el-upload>
      
      <div class="upload-options">
        <el-checkbox v-model="updateExisting">更新已存在的數據</el-checkbox>
        <el-tooltip 
          content="如果勾選，已存在的數據會被更新；如果不勾選，則跳過已存在的數據"
          placement="top">
          <el-icon><i-ep-question-filled /></el-icon>
        </el-tooltip>
      </div>
      
      <div v-if="isUploading" class="upload-progress">
        <el-progress :percentage="uploadProgress" :status="uploadStatus"></el-progress>
        <div class="progress-text">正在處理，請稍候...</div>
      </div>
      
      <div v-if="resultMessage" :class="['upload-result', { 'success': isSuccess, 'error': !isSuccess }]">
        {{ resultMessage }}
      </div>
      
      <div class="file-path-import">
        <h3>從服務器路徑導入</h3>
        <el-input v-model="filePath" placeholder="請輸入服務器上的JSON文件路徑"></el-input>
        <el-button type="primary" @click="importFromPath" :disabled="isUploading || !filePath">導入</el-button>
      </div>
    </el-card>

    <el-card class="guide-card">
      <template #header>
        <div class="card-header">
          <h3>數據導入指南</h3>
        </div>
      </template>
      
      <div class="guide-content">
        <h4>JSON文件格式要求：</h4>
        <p>要導入的JSON文件應該是一個包含鞋子信息的數組，每個鞋子對象需包含以下字段：</p>
        <ul>
          <li><strong>id</strong>: 鞋子的唯一標識符</li>
          <li><strong>title</strong>: 鞋子的標題或名稱</li>
          <li><strong>images</strong>: 包含圖片信息的數組，每個圖片對象需包含<code>url</code>字段</li>
          <li><strong>collections</strong>: 包含分類信息的对象</li>
        </ul>
        
        <h4>示例結構：</h4>
        <pre>
[
  {
    "id": "189745021",
    "title": "科恩Keen Jasper Rocks SP賈斯珀都市系列",
    "model_number": "",
    "images": [
      {
        "data_id": "71183582",
        "url": "https://example.com/image1.jpg",
        "title": ""
      },
      {
        "data_id": "71183581",
        "url": "https://example.com/image2.jpg",
        "title": ""
      }
    ],
    "collections": {
      "4707638": {
        "id": "4707638",
        "href": "/collections/4707638",
        "title": "科恩/Keen",
        "full_url": "https://example.com/collections/4707638"
      }
    },
    "detailed_description": "复古低幫系帶户外風露營徒步登山運動训练鞋"
  }
]
        </pre>
      </div>
    </el-card>

    <el-card class="history-card" v-if="importHistory.length > 0">
      <template #header>
        <div class="card-header">
          <h3>導入歷史</h3>
        </div>
      </template>
      
      <el-table :data="importHistory" style="width: 100%">
        <el-table-column prop="filename" label="文件名" width="180" />
        <el-table-column prop="timestamp" label="時間" width="180" />
        <el-table-column prop="status" label="狀態">
          <template #default="scope">
            <el-tag :type="scope.row.status === 'success' ? 'success' : 'danger'">
              {{ scope.row.status === 'success' ? '成功' : '失敗' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="message" label="消息" />
      </el-table>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import axios from 'axios'

// 创建API实例，避免重复URL问题
const apiClient = axios.create({
  baseURL: '/api/v1',  // 使用相对路径通过代理访问
  timeout: 60000,      // 导入需要更长时间，设置为60秒
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
})

// 添加请求拦截器，用于调试
apiClient.interceptors.request.use(config => {
  return config;
}, error => {
  return Promise.reject(error);
});

// 添加响应拦截器，用于调试
apiClient.interceptors.response.use(response => {
  return response;
}, error => {
  console.error('导入API错误:', error.message);
  if (error.response) {
    console.error('状态码:', error.response.status);
    console.error('响应数据:', error.response.data);
  }
  return Promise.reject(error);
});

// 基本數據
const isUploading = ref(false)
const uploadProgress = ref(0)
const uploadStatus = ref('')
const resultMessage = ref('')
const isSuccess = ref(false)
const updateExisting = ref(true)
const filePath = ref('')
const importHistory = ref([])
const uploadHeaders = ref({})

// 檢查文件上傳前操作
const beforeUpload = (file) => {
  // 檢查文件大小 (限制為 15MB)
  const isLt15M = file.size / 1024 / 1024 < 15
  if (!isLt15M) {
    ElMessage.error('文件大小不能超過 15MB!')
    return false
  }
  
  // 檢查文件類型
  const isJSON = file.type === 'application/json' || file.name.endsWith('.json')
  if (!isJSON) {
    ElMessage.error('只能上傳JSON文件!')
    return false
  }
  
  // 開始上傳
  isUploading.value = true
  uploadProgress.value = 0
  uploadStatus.value = ''
  resultMessage.value = ''
  
  // 使用相对路径请求，通过Vue代理转发
  const uploadUrl = '/import/upload-json'
  const queryParams = `?update_existing=${updateExisting.value}`
  
  // 使用API客户端处理上传
  simulateUpload(file, uploadUrl + queryParams)
  
  // 阻止默認上傳
  return false
}

// 修改 axios 請求配置，添加錯誤處理
const simulateUpload = (file, url) => {
  const formData = new FormData()
  formData.append('file', file)
  
  // 設置上傳進度
  const config = {
    onUploadProgress: progressEvent => {
      uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total)
    },
    headers: uploadHeaders.value
  }
  
  apiClient.post(url, formData, config)
    .then(response => {
      handleUploadSuccess(response.data, file)
    })
    .catch(error => {
      console.error('上傳錯誤:', error)
      handleUploadError(error, file)
    })
}

// 上傳成功處理
const handleUploadSuccess = (response, file) => {
  isUploading.value = false
  uploadProgress.value = 100
  uploadStatus.value = 'success'
  isSuccess.value = true
  resultMessage.value = response.message || '上傳成功!'
  
  // 添加到歷史記錄
  importHistory.value.unshift({
    filename: file.name,
    timestamp: new Date().toLocaleString(),
    status: 'success',
    message: resultMessage.value
  })
  
  // 提示成功
  ElMessage({
    message: resultMessage.value,
    type: 'success'
  })
}

// 上傳失敗處理
const handleUploadError = (error, file) => {
  isUploading.value = false
  uploadProgress.value = 100
  uploadStatus.value = 'exception'
  isSuccess.value = false
  
  // 解析錯誤消息
  let errorMessage = '上傳失敗!'
  if (error.response) {
    errorMessage = error.response.data?.detail || errorMessage
  } else if (error.message) {
    errorMessage = error.message
  }
  
  resultMessage.value = errorMessage
  
  // 添加到歷史記錄
  importHistory.value.unshift({
    filename: file ? file.name : '未知文件',
    timestamp: new Date().toLocaleString(),
    status: 'error',
    message: errorMessage
  })
  
  // 提示失敗
  ElMessage.error(errorMessage)
}

// 從指定路徑導入
const importFromPath = () => {
  if (!filePath.value) {
    ElMessage.warning('請輸入文件路徑')
    return
  }
  
  isUploading.value = true
  uploadProgress.value = 0
  uploadStatus.value = ''
  resultMessage.value = ''
  
  const data = new FormData()
  data.append('file_path', filePath.value)
  data.append('update_existing', updateExisting.value)
  
  // 模擬進度
  const timer = setInterval(() => {
    if (uploadProgress.value < 90) {
      uploadProgress.value += 10
    }
  }, 300)
  
  apiClient.post('/import/import-from-path', data)
    .then(response => {
      clearInterval(timer)
      isUploading.value = false
      uploadProgress.value = 100
      uploadStatus.value = 'success'
      isSuccess.value = true
      resultMessage.value = response.data.message || '導入成功!'
      
      // 添加到歷史記錄
      importHistory.value.unshift({
        filename: filePath.value,
        timestamp: new Date().toLocaleString(),
        status: 'success',
        message: resultMessage.value
      })
      
      // 提示成功
      ElMessage({
        message: resultMessage.value,
        type: 'success'
      })
    })
    .catch(error => {
      clearInterval(timer)
      isUploading.value = false
      uploadProgress.value = 100
      uploadStatus.value = 'exception'
      isSuccess.value = false
      
      // 解析錯誤消息
      let errorMessage = '導入失敗!'
      if (error.response) {
        errorMessage = error.response.data?.detail || errorMessage
      } else if (error.message) {
        errorMessage = error.message
      }
      
      resultMessage.value = errorMessage
      
      // 添加到歷史記錄
      importHistory.value.unshift({
        filename: filePath.value,
        timestamp: new Date().toLocaleString(),
        status: 'error',
        message: errorMessage
      })
      
      // 提示失敗
      ElMessage.error(errorMessage)
    })
}

// 初始化頁面
onMounted(() => {
  // 设置上传头信息
  uploadHeaders.value = {
    'Accept': 'application/json'
  }
})
</script>

<style scoped>
.import-data-container {
  max-width: 900px;
  margin: 20px auto;
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
  text-align: center;
}

.page-header h1 {
  font-size: 24px;
  color: #303133;
}

.page-header .description {
  color: #606266;
}

.upload-card, .guide-card, .history-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h3 {
  margin: 0;
  font-size: 18px;
}

.upload-area {
  width: 100%;
  margin-bottom: 20px;
}

.upload-options {
  margin: 15px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.upload-progress {
  margin: 20px 0;
}

.progress-text {
  text-align: center;
  margin-top: 10px;
  color: #606266;
}

.upload-result {
  padding: 10px;
  border-radius: 4px;
  margin: 15px 0;
}

.upload-result.success {
  background-color: #f0f9eb;
  color: #67c23a;
  border: 1px solid #e1f3d8;
}

.upload-result.error {
  background-color: #fef0f0;
  color: #f56c6c;
  border: 1px solid #fbc4c4;
}

.file-path-import {
  margin-top: 30px;
  border-top: 1px solid #ebeef5;
  padding-top: 20px;
}

.file-path-import h3 {
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 16px;
}

.file-path-import .el-input {
  margin-bottom: 15px;
}

.guide-content {
  font-size: 14px;
}

.guide-content h4 {
  margin-top: 15px;
  margin-bottom: 8px;
}

.guide-content pre {
  background-color: #f8f8f8;
  border-radius: 4px;
  padding: 10px;
  overflow-x: auto;
  font-size: 12px;
}

.guide-content code {
  background-color: #f0f0f0;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: monospace;
}
</style> 